import { format } from 'date-fns';
import { getCell, parsePastedDate } from './parsePastedGrid';
import type { ComparableTariff, ExcelPasteIssue, PastedPeriod } from './types';

/** Lines 2 to 8 hold periods 1 to 7, unless the block starts with extra lines. */
export const FIRST_PERIOD_LINE = 2;
export const PERIOD_LINES_COUNT = 7;

const periodsOverlap = (a: PastedPeriod, b: PastedPeriod) =>
  a.startDate <= b.endDate && b.startDate <= a.endDate;

const toDate = (value: Date | string | null | undefined): Date | null => {
  if (value == null || value === '') return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

/**
 * Reads the period lines of a block. A line with both dates is a period, an
 * empty line is skipped. Periods come back sorted by start date because the
 * form schemas compare each period with the previous one.
 */
export function parsePastedPeriods(
  rows: string[][],
  firstPeriodLine = FIRST_PERIOD_LINE,
): {
  periods: PastedPeriod[];
  errors: ExcelPasteIssue[];
} {
  const errors: ExcelPasteIssue[] = [];
  const numbered: Array<PastedPeriod & { period: number }> = [];

  for (let period = 1; period <= PERIOD_LINES_COUNT; period++) {
    const line = firstPeriodLine + period - 1;
    const rawStart = getCell(rows, line, 0);
    const rawEnd = getCell(rows, line, 1);
    if (rawStart === '' && rawEnd === '') continue;

    if (rawStart === '') {
      errors.push({ code: 'periodMissingStart', params: { period } });
      continue;
    }
    if (rawEnd === '') {
      errors.push({ code: 'periodMissingEnd', params: { period } });
      continue;
    }

    const startDate = parsePastedDate(rawStart);
    const endDate = parsePastedDate(rawEnd);
    if (!startDate || !endDate) {
      errors.push({
        code: 'invalidDate',
        params: { line, value: !startDate ? rawStart : rawEnd },
      });
      continue;
    }
    if (startDate > endDate) {
      errors.push({ code: 'periodStartAfterEnd', params: { period } });
      continue;
    }
    numbered.push({ startDate, endDate, period });
  }

  if (errors.length === 0 && numbered.length === 0) {
    errors.push({
      code: 'noPeriod',
      params: {
        from: firstPeriodLine,
        to: firstPeriodLine + PERIOD_LINES_COUNT - 1,
      },
    });
  }

  for (let i = 0; i < numbered.length; i++) {
    for (let j = i + 1; j < numbered.length; j++) {
      if (periodsOverlap(numbered[i], numbered[j])) {
        errors.push({
          code: 'periodsOverlap',
          params: { first: numbered[i].period, second: numbered[j].period },
        });
      }
    }
  }

  const periods = numbered
    .map(({ startDate, endDate }) => ({ startDate, endDate }))
    .sort((a, b) => a.startDate.getTime() - b.startDate.getTime());

  return { periods, errors };
}

/**
 * Warns when pasted periods share days with another tariff of the same scope
 * (category, pension prices or supplement). Not blocking: the grids sometimes
 * do it on purpose, but it usually means a block was pasted in the wrong card.
 */
export function findOverlapsWithOtherTariffs(
  periods: PastedPeriod[],
  otherTariffs: ComparableTariff[],
): ExcelPasteIssue[] {
  const warnings: ExcelPasteIssue[] = [];

  for (const { name, periods: otherPeriods } of otherTariffs) {
    let firstSharedDay: Date | null = null;
    for (const period of periods) {
      for (const other of otherPeriods) {
        const startDate = toDate(other.startDate);
        const endDate = toDate(other.endDate);
        if (!startDate || !endDate) continue;
        if (!periodsOverlap(period, { startDate, endDate })) continue;
        const sharedDay =
          period.startDate > startDate ? period.startDate : startDate;
        if (!firstSharedDay || sharedDay < firstSharedDay) {
          firstSharedDay = sharedDay;
        }
      }
    }
    if (firstSharedDay) {
      warnings.push({
        code: 'overlapsOtherTariff',
        params: { date: format(firstSharedDay, 'dd/MM/yy'), tariff: name },
      });
    }
  }

  return warnings;
}
